<template>
  <ContentPage variant="plain">
    <template #header>
      <div class="flex flex-col gap-2">
        <UiInput v-model="search" placeholder="Search name">
          <template #prepend>
            <IconSearchLine />
          </template>
        </UiInput>
        <Filter
          v-model="filterValues"
          storage-key="delegates"
          :fields="filterFields"
          :loading="isLoadingManagers || isLoadingMe"
        />
      </div>
    </template>
    <UiList
      :items="companies"
      :loading="isListLoading"
      :finished="!hasNextPage"
      :empty-title="emptyTitle"
      :empty-description="emptyDescription"
      :on-refresh="refetch"
      @load-more="fetchNextPage"
    >
      <template #default="{ item }">
        <CompanyCard :company="item" @participant-click="onParticipantClick" />
      </template>
    </UiList>
  </ContentPage>
</template>
<script lang="ts" setup>
import { computed, onMounted, shallowRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useCompaniesInfiniteQuery } from '@/queries/companies'
import { useSearchQuery } from '@/composables/search-query'
import { useEventTitle } from '@/composables/event-title'
import type { Participant } from '@/types'
import { useMeQuery, usePersonalManagersQuery } from '@/queries/participants'
import type { FilterOption, FilterSelectField } from '@/types/filter'

// Router
const router = useRouter()

// Refs
const filterValues = shallowRef<Record<string, string | string[]>>({})
const isReadyToQuery = shallowRef(false)

// Composables
const { search, debouncedSearch } = useSearchQuery()
useEventTitle()

// Queries
const { data: managers, isLoading: isLoadingManagers } = usePersonalManagersQuery()
const { data, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
  useCompaniesInfiniteQuery(
    computed(() => ({
      q: debouncedSearch.value || undefined,
      filter: {
        personal_manager_participant_id:
          filterValues.value.personal_manager !== 'all'
            ? (filterValues.value.personal_manager as string)
            : undefined,
      },
    })),
    { enabled: isReadyToQuery },
  )
const {
  data: me,
  isLoading: isLoadingMe,
  isSuccess: isMeSuccess,
  isError: isMeError,
} = useMeQuery()

// Computed
const companies = computed(() => data.value?.pages.flatMap((p) => p.data) ?? [])
const isListLoading = computed(
  () =>
    isFetchingNextPage.value ||
    !isReadyToQuery.value ||
    (isFetching.value && !companies.value.length),
)
const isFiltering = computed(
  () =>
    !!debouncedSearch.value ||
    (!!filterValues.value.personal_manager && filterValues.value.personal_manager !== 'all'),
)
const emptyTitle = computed(() => (isFiltering.value ? 'No Results Found' : 'No Companies'))
const emptyDescription = computed(() =>
  isFiltering.value
    ? 'Try changing your search or filter options to find what you’re looking for'
    : 'Companies appear here later',
)
const filterFields = computed<Array<FilterSelectField>>(() => {
  const options = (managers.value?.data ?? [])
    .filter((manager) => manager.id !== me.value?.id)
    .map((manager) => ({
      name: manager.name,
      value: manager.id,
      group: 'Managers',
    }))

  const quickOptions: Array<FilterOption> = []

  if (me.value) {
    quickOptions.push({
      name: 'My Companies',
      value: me.value.id,
    })
  }

  quickOptions.push({
    name: 'All',
    value: 'all',
  })

  return [
    {
      key: 'personal_manager',
      label: 'Personal Manager',
      type: 'select',
      default: me.value?.id ?? 'all',
      quickOptions,
      options,
    },
  ]
})

// onMounted: Filter уже смонтирован и filterValues заполнен корректными дефолтами.
// Если me уже загружен из кеша — сразу разрешаем запрос.
onMounted(() => {
  if (isMeSuccess.value || isMeError.value) {
    isReadyToQuery.value = true
  }
})

// После загрузки me: flush:'post' гарантирует что цепочка
// useFilter → Filter.vue → filterValues уже выполнилась.
watch(
  [isMeSuccess, isMeError],
  ([success, error]) => {
    if (success || error) {
      isReadyToQuery.value = true
    }
  },
  { flush: 'post' },
)

// Handlers
function onParticipantClick(participant: Participant) {
  router.push({ name: 'delegate', params: { delegate_id: participant.id } })
}

// Page
definePage({
  name: 'companies',
  meta: {
    title: 'Companies',
    disableBack: true,
  },
})
</script>
